import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { GlobalVariable } from '../helpers/settings';
import { Result } from '../models/general';
import { StockFilter, StockHistory } from '../models/stock-history';
import { AuthService } from './auth.service';
import { Unity } from '../models/unity';
import { PaginationOption } from '../models/pagination.model';
import { RefreshVerificationService } from './refresh-verification.service';

export interface PaginationData {
	docs: StockHistory[];
	total: number;
	limit: number;
	page: number;
	pages: number;
}

@Injectable({
	providedIn: 'root'
})
export class StockProductService {
	readonly BASE_URL = `${GlobalVariable.server.host}/stocksLine`;

	constructor(
		private httpClient: HttpClient,
		private authService: AuthService,
		private refreshVerifService: RefreshVerificationService
	) {}
	find(projectId: string, fromAppComponent: boolean, route, action: string): Observable<{ total: number }> {
		const params = new HttpParams()
			.set('fromAppComponent', fromAppComponent)
			.set('componentRoute', route)
			.set('projectId', projectId)
			.set('actionName', action);
		return this.refreshVerifService.buidResult(
			this.httpClient.get<{ total: number }>(`${this.BASE_URL}/find/${projectId}`, { params })
		);
	}

	findById(id: string, projectId: string, searchProduct: string = null): Observable<StockHistory[]> {
		const params = searchProduct
			? new HttpParams().set('projectId', projectId).set('searchProduct', searchProduct)
			: new HttpParams().set('projectId', projectId);
		return this.refreshVerifService.buidResult(
			this.httpClient.get<StockHistory[]>(`${this.BASE_URL}/find-by-id/${id}`, { params })
		);
	}

	getProductsInStock(
		projectId: string,
		searchProduct: string = null,
		paginationOption: PaginationOption,
		filter: StockFilter
	): Observable<PaginationData> {
		const queryParams = searchProduct ? `?searchProduct=${searchProduct}` : '';
		return this.refreshVerifService.buidResult(
			this.httpClient.post<PaginationData>(`${this.BASE_URL}/get-products-inStock/${projectId + queryParams}`, {
				paginationOption,
				filter
			})
		);
	}

	getDistinctProduct(projectId: string, action): Observable<StockHistory[]> {
		const params = new HttpParams().set('actionName', action);
		return this.refreshVerifService.buidResult(
			this.httpClient.get<StockHistory[]>(`${this.BASE_URL}/find-stock-products/${projectId}`, { params })
		);
	}

	getLastOfDeposits(projectId: string, action): Observable<{ product; addHistories }[]> {
		const params = new HttpParams().set('actionName', action);
		return this.refreshVerifService.buidResult(
			this.httpClient.get<{ product; addHistories }[]>(`${this.BASE_URL}/get-last-of-deposits/${projectId}`, {
				params
			})
		);
	}

	getNotification(): Observable<any> {
		return this.refreshVerifService.buidResult(this.httpClient.get<any>(`${this.BASE_URL}/get-notifications`));
	}
	getNotificationByProject(projectId): Observable<any> {
		return this.refreshVerifService.buidResult(
			this.httpClient.get<any>(`${this.BASE_URL}/get-notifications-by-project/${projectId}`)
		);
	}

	getDeadLineProduct(projectId: string): Observable<StockHistory[]> {
		return this.refreshVerifService.buidResult(
			this.httpClient.get<StockHistory[]>(`${this.BASE_URL}/get-deadLine-product/${projectId}`)
		);
	}

	create(stockHistories: Partial<StockHistory>[], voucher: string = null): Observable<Result> {
		const queryParams = voucher ? `?voucher=${voucher}` : '';
		for (let stockHistory of stockHistories) {
			stockHistory.user = this.authService.getCurrentUser();
		}
		return this.refreshVerifService.buidResult(
			this.httpClient.post<Result>(`${this.BASE_URL}/create${queryParams}`, stockHistories)
		);
	}

	createWithdrawFromOrder(params): Observable<Result> {
		let { stockLines, isFromEdit = false } = params;
		for (let stockHistory of stockLines) {
			stockHistory.user = this.authService.getCurrentUser();
		}
		return this.refreshVerifService.buidResult(
			this.httpClient.post<Result>(`${this.BASE_URL}/create-withdraw-from-order`, {
				project: stockLines[0]?.project,
				...params
			})
		);
	}

	checkWithdrawProductsQuantity(params): Observable<any> {
		let { withdrawProducts, stockModel } = params;
		return this.refreshVerifService.buidResult(
			this.httpClient.post<any>(`${this.BASE_URL}/check-withdraw-products-quantity`, {
				project: withdrawProducts[0]?.project,
				withdrawProducts,
				stockModel
			})
		);
	}

	edit(stockHistory: Partial<StockHistory>, projectId: string): Observable<Result> {
		const params = new HttpParams().set('projectId', projectId);
		return this.refreshVerifService.buidResult(
			this.httpClient.put<Result>(`${this.BASE_URL}/edit`, stockHistory, { params })
		);
	}

	delete(stockHistoryId: string, projectId: string): Observable<Result> {
		const params = new HttpParams().set('projectId', projectId);
		return this.refreshVerifService.buidResult(
			this.httpClient.delete<Result>(`${this.BASE_URL}/delete/${stockHistoryId}`, { params })
		);
	}
}
